import { AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, ToastNotificationAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnCancel, OnChange, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import BaseController from '../BaseController';
import { Inject } from '@odinjs/odin';
import HealthInsuranceCategory from '../../interfaces/health-insurance-category';
import HealthInsurancePlan from '../../interfaces/health-insurance-plan';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import UserContext from '../../utils/user-context';
import { ContextType } from '@philips-internal/oswald-ui-core';
import { FragmentNames, InsuranceAutocompleteModalAliases } from '../../codegen';
import HealthInsurance from '../../interfaces/health-insurance';
import PseudonymAppointmentModel from '../pseudonym-appointments/pseudonym-appointments-model';
import { applyInsuranceFieldVisibility, getInsuranceFieldVisibility, InsuranceFieldVisibility } from '../../utils/insurance-fields';

@Controller({ fragment: FragmentNames.INSURANCE_AUTOCOMPLETE_MODAL })
export default class InsuranceAutocompleteModalController extends BaseController {

  insuranceFieldVisibilityPromise?: Promise<InsuranceFieldVisibility>;

  @Inject(InsuranceAutocompleteModalAliases.FIELD_PLAN)
    fieldPlan!: FieldAPI

  @Inject(InsuranceAutocompleteModalAliases.FIELD_CATEGORY)
    fieldCategory!: FieldAPI;

  @Inject(InsuranceAutocompleteModalAliases.FIELD_CHECKBOX_SAVE_INSURANCE)
    fieldCheckboxSaveInsurace!: FieldAPI;

  @Inject(InsuranceAutocompleteModalAliases.FETCH_CATEGORY)
    categoryFetch!: FetchAPI;

  @Inject(InsuranceAutocompleteModalAliases.FETCH_PLAN)
    planFetch!: FetchAPI;

  @Inject(InsuranceAutocompleteModalAliases.AUTOCOMPLETE_CATEGORY)
    autoCompleteInsuranceCategory!: AutocompleteAPI;

  @Inject(InsuranceAutocompleteModalAliases.AUTOCOMPLETE_PLAN)
    autoCompleteInsurancePlan!: AutocompleteAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject(InsuranceAutocompleteModalAliases.FIELD_HEALTH_CARD_NUMBER)
    fieldHealthCardNumber!: FieldAPI;

  @Inject(InsuranceAutocompleteModalAliases.FORM_INSURANCE)
    formInsurance!: FormAPI;

  @Inject(InsuranceAutocompleteModalAliases.TOAST_NOTIFICATION)
    toastNotification!: ToastNotificationAPI;

  @OnMount({target: InsuranceAutocompleteModalAliases.LAYOUT_INSURANCES})
  handleLoadModal(): void {
    const patientId = Number(UserContext.patientId);
    if(patientId === 0) {
      this.fieldCheckboxSaveInsurace.setVisible(false);
    }
  }

  @OnChange({ target: InsuranceAutocompleteModalAliases.AUTOCOMPLETE_HEALTH })
  handleChangeInsurance(event: ChangeEvent<HealthInsurance>): void {
    const autocomplete = event.target as unknown as AutocompleteAPI;
    this.clearAutocompletesOnChange(autocomplete);
    if (event?.value?.cdConvenio) {
      this.context.set({ cdConvenio: event.value.cdConvenio, ieInsurancePrivate: event.value.ieInsurancePrivate });
      if (this.fieldCategory.getVisible()) {
        this.autoCompleteInsuranceCategory.setEnabled(true);
        this.showInsuranceCategory(event.value.cdConvenio, '');
      }
      this.autoCompleteInsurancePlan.setEnabled(true);
      this.showInsurancePlan(event.value.cdConvenio, '');
    } else {
      if (this.fieldCategory.getVisible()) {
        this.autoCompleteInsuranceCategory.setEnabled(false);
      }
      this.autoCompleteInsurancePlan.setEnabled(false);
    }
  }

  @OnChange({ target: InsuranceAutocompleteModalAliases.AUTOCOMPLETE_CATEGORY })
  onChangeCategory(event: ChangeEvent<HealthInsuranceCategory>): void {
    if (event?.value) {
      this.showInsurancePlan(event.value.cdConvenio, event.value.cdCategoria);
    } else if (event?.oldValue) {
      this.showInsurancePlan(event.oldValue.cdConvenio, '');
    }
  }

  @OnChange({ target: InsuranceAutocompleteModalAliases.AUTOCOMPLETE_PLAN })
  onChangePlan(event: ChangeEvent<HealthInsurancePlan>): void {
    if (!this.fieldCategory.getVisible()) return;
    if (event?.value) {
      this.showInsuranceCategory(event.value.cdConvenio, event.value.cdPlano);
    } else if (event?.oldValue) {
      this.showInsuranceCategory(event.oldValue.cdConvenio, '');
    }
  }

  @OnAfterFetch({ target: InsuranceAutocompleteModalAliases.FETCH_CATEGORY })
  handleAfterFetchCategory(): void {
    const data = this.categoryFetch.getData() as HealthInsuranceCategory[] | undefined;
    if (data?.length === 1) {
      this.fieldCategory.setValue(data[0]);
    }
  }

  @OnBeforeFetch({ target: InsuranceAutocompleteModalAliases.FETCH_HEALTH })
  handleBeforeFetchHealth(event: FetchEvent): void {
    const establishmentCode = event.target as FetchAPI;
    establishmentCode.setParameters({ establishmentCode : PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment || 0 });
  }

  clearAutocompletesOnChange(autocomplete: AutocompleteAPI): void {
    switch (autocomplete.getAlias()) {
      case InsuranceAutocompleteModalAliases.AUTOCOMPLETE_HEALTH:
        this.fieldPlan.setValue({});
        this.fieldCategory.setValue({});
        if (this.fieldCategory.getVisible()) {
          this.autoCompleteInsuranceCategory.setValue({});
        }
        this.autoCompleteInsurancePlan.setValue({});
        break;
      default:
        break;
    }
  }

  showInsuranceCategory(cdConvenio: string, planCode: string): void {
    setTimeout(() => {
      let establishmentCode = 0;
      if (PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment != 0) {
        establishmentCode = PseudonymAppointmentModel.stepOne.appointmentEstablishment.cdEstablishment;
      }
      this.categoryFetch.setParameters({ cdConvenio, establishmentCode, planCode });
      this.categoryFetch.forceFetch();
    });
  }

  showInsurancePlan(cdConvenio: string, categoryCode: string): void {
    let establishmentCode = 0;
    if (PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment != 0) {
      establishmentCode = PseudonymAppointmentModel.stepOne.appointmentEstablishment.cdEstablishment;
    }
    if (cdConvenio) {
      this.autoCompleteInsurancePlan.setEnabled(true);
      setTimeout(() => {
        const validateRules = true;
        this.planFetch.setParameters({ categoryCode, cdConvenio, establishmentCode, validateRules});
        this.planFetch.forceFetch();
      });
    }
  }

  @OnSubmit({target: InsuranceAutocompleteModalAliases.FORM_INSURANCE})
  handleSubmitFormInsurnace(event: SubmitEvent): void {
    if (event) {
      this.submittableFragmentContext.submit(new SubmitEvent({ record: event.record }));
    }
  }

  @OnCancel({target: InsuranceAutocompleteModalAliases.FORM_INSURANCE})
  handleCancelFormInsurance(): void {
    this.submittableFragmentContext.submit(new SubmitEvent());
  }

  @OnMount({target: InsuranceAutocompleteModalAliases.FORM_INSURANCE})
  handleMountFormInsurance(): void {
    this.getInsuranceFieldVisibility().then((visibility) => {
      applyInsuranceFieldVisibility({
        categoryField: this.fieldCategory,
        healthCardNumberField: this.fieldHealthCardNumber,
        planField: this.fieldPlan,
      }, visibility);
    });
    this.toastNotification.setVisible(PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment !== 0);
    this.formInsurance.validate();
  }

  getInsuranceFieldVisibility(): Promise<InsuranceFieldVisibility> {
    this.insuranceFieldVisibilityPromise ??= getInsuranceFieldVisibility();
    return this.insuranceFieldVisibilityPromise;
  }

}
